import posthog from "posthog-js";
import { appConfig } from "@/config";
import type { UserRole } from "@/utils/userRole";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

// Gated on key presence, mirroring the Sentry DSN-presence gate in src/index.tsx.
export const isPostHogEnabled = Boolean(POSTHOG_KEY);

// Strips the entire query string and hash from a URL before it lands in a
// captured event property or the session-replay player's URL display — see
// POSTHOG_ANALYTICS_PLAN.md §5.4/§12. This app keeps real state in the URL
// (filters, search text, propertyIds, and Keycloak's OAuth code/state on the
// post-login redirect), so an allowlist of "known sensitive" param names is
// the wrong shape here: it silently misses whatever's added next. Query-string
// values that are meaningful for analytics (filters, sort, property
// selection) are already captured as structured properties passed directly
// on the relevant active_leads_* events — see trackActiveLeadsEvent call
// sites — so nothing analytically useful is lost by scrubbing it from the
// raw URL.
//
// PostHog's own sentinel values for "no referrer" — not URLs. They must pass
// through untouched: `new URL("$direct", origin)` resolves happily to
// `<origin>/$direct`, which would make every direct visit look like a referral
// from our own domain and break channel attribution project-wide.
const REFERRER_SENTINELS = new Set(["$direct", "$search"]);

function scrubUrl(value: string): string {
  if (REFERRER_SENTINELS.has(value)) return value;
  try {
    const url = new URL(value, window.location.origin);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    // Fail closed: an unparseable "URL" is unexpected input we can't
    // guarantee is free of sensitive query values.
    return "[redacted]";
  }
}

// Scrubbed on every captured event. The `$initial_*` variants are the
// first-touch snapshot PostHog persists as person properties, so an unscrubbed
// one keeps its query string forever rather than just for one event.
const URL_PROPERTY_KEYS = [
  "$current_url",
  "$referrer",
  "$external_click_url",
  "$initial_current_url",
  "$initial_referrer",
] as const;

function scrubUrlsIn(container: Record<string, unknown> | undefined): void {
  if (!container) return;
  for (const key of URL_PROPERTY_KEYS) {
    const value = container[key];
    if (typeof value === "string") {
      container[key] = scrubUrl(value);
    }
  }
}

// Path segments known to be static route parts (not IDs) across the
// activeleads API surface — see leadDetailPath/commentPath/etc. in
// ActionApis/ApiClient.tsx. Anything else in the path is templated out
// before being sent as an analytics property, since it may be a
// property ID or Salesforce record ID.
const STATIC_ENDPOINT_SEGMENTS = new Set([
  "properties",
  "leads",
  "comments",
  "attachments",
  "download",
  "filter-options",
  "summary",
  "export",
]);

/** Templates a request path for use as an event property — e.g.
 * `/properties/123/leads/abc` → `/properties/:id/leads/:id`. */
export function templatizeEndpoint(url: string | undefined): string {
  if (!url) return "unknown";
  const path = url.split("?")[0];
  return path
    .split("/")
    .map((segment) =>
      segment && !STATIC_ENDPOINT_SEGMENTS.has(segment) ? ":id" : segment
    )
    .join("/");
}

// Single free-tier PostHog project shared across environments — tag every
// event/recording so Dev and Prod traffic can be told apart in the UI
// (filter/exclude by this property in Insights, Recordings, etc.). Re-run
// after posthog.reset() too: reset() clears persistence (persistence.clear()
// in the SDK), which wipes registered super properties along with identity —
// without this, events lose `environment` after any logout that doesn't
// happen to trigger a full page reload (e.g. a forced sign-out on token
// refresh failure, as opposed to the redirect-based Keycloak logout flow).
function registerAppContext(): void {
  posthog.register({ environment: appConfig.environment });
}

let postHogInitialized = false;

// Called from the Keycloak auth bootstrap once keycloak.init() resolves —
// not at module load — so the very first $pageview/session-replay capture
// happens after Keycloak has stripped the OAuth `code`/`state` callback
// params from the URL via its own history.replaceState, instead of racing
// it. Idempotent: Keycloak's init effect only runs once, but this guards
// against StrictMode double-invocation regardless.
export function initializePostHog(): void {
  if (!isPostHogEnabled || postHogInitialized) return;
  postHogInitialized = true;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    // No PostHog feature flags are used anywhere in src/ (the app's own
    // `featureFlags` in @/config is env-var driven and unrelated), and the
    // project reports hasFeatureFlags: false. This specifically disables flag
    // *evaluation* (its request carries a `person_properties` payload that
    // bypasses before_send below, so it was shipping the unscrubbed
    // $initial_current_url) — NOT `advanced_disable_flags`, which disables
    // the SDK's whole remote-config fetch. Session recording's
    // server-side "enabled" bit only ever arrives via that remote-config
    // response (posthog-js: SessionRecording.onRemoteConfig), so
    // advanced_disable_flags silently made startSessionRecording() below a
    // permanent no-op — confirmed via posthog-js source, not guessed.
    advanced_disable_feature_flags: true,
    // "history_change" (not the plain `true` default) so SPA route changes via
    // react-router's pushState/replaceState fire $pageview too, not just the
    // initial hard load.
    capture_pageview: "history_change",
    autocapture: true,
    // data-ph-mask (below) only governs session-replay text masking — it has
    // no effect on autocapture, which is a separate capture pipeline. Without
    // these, a click on e.g. a lead card (LeadsTable.tsx) sends that
    // element's text — contact name, quoted revenue — to PostHog as an
    // autocapture event property, bypassing the replay masking entirely.
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Off by default portal-wide — this module initializes globally (see
    // initializePostHog's call site in KeycloakContext.tsx), not scoped to
    // any route. The data-ph-mask/maskTextSelector masking below was only
    // ever reviewed for Active Leads' components (contact names, revenue,
    // CRM notes) — recording other pages by default would replay their
    // content unmasked. Recording is opt-in per route via
    // startSessionRecording()/stopSessionRecording() below, called only by
    // pages whose replay masking has actually been reviewed.
    disable_session_recording: true,
    session_recording: {
      // Belt-and-suspenders with the per-field data-ph-mask attributes below:
      // any text-input the user types (search, comment box) is masked outright.
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]",
      // Also scrubs the page URL shown in the replay player itself, not just
      // captured network request URLs — confirmed by PostHog maintainers as
      // the (non-obvious) fix for this, see PostHog/posthog#39197. The field
      // is `.name` (this type wraps the standard PerformanceEntry shape), not
      // `.url` — the deprecated NetworkRequest type has `.url` instead.
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: scrubUrl(request.name),
      }),
    },
    // sanitize_properties is deprecated in favor of before_send (posthog-js
    // config types). This is the analytics-property side of URL scrubbing;
    // maskCapturedNetworkRequestFn above handles the replay-recording side.
    // $set/$set_once are walked as well as properties: they sit at the top
    // level of the event, not inside properties, so scrubbing only the latter
    // left $initial_current_url carrying the query string into a *persistent*
    // person property.
    before_send: (event) => {
      if (!event) return event;
      scrubUrlsIn(event.properties);
      scrubUrlsIn(event.$set);
      scrubUrlsIn(event.$set_once);
      return event;
    },
  });
  // Active Leads used to call posthog.group("property", propertyIds[0]),
  // associating a single property with the session. That association was
  // persisted client-side (localStorage) and kept being sent as $groups /
  // $groups_0 on every event even after the code stopped calling group() —
  // so existing users' browsers may still carry it. Property context now
  // travels as an explicit `property_ids` property passed directly on each
  // relevant active_leads_* event (see trackActiveLeadsEvent call sites),
  // not as a super property — a super property is global/persistent (shared
  // across browser tabs via localStorage, and outlives the selection that
  // produced it) which caused stale/leaked property_ids on unrelated events.
  //
  // One-time migration, not permanent cleanup: resetGroups() runs on every
  // init otherwise (postHogInitialized only guards within a single page
  // load), which would silently wipe out any legitimate posthog.group() use
  // some other page adds later, portal-wide, on its very next load. Bump the
  // version suffix if a similar one-off cleanup is ever needed again.
  const LEGACY_GROUPS_CLEARED_KEY = "posthog_legacy_groups_cleared_v1";
  if (!localStorage.getItem(LEGACY_GROUPS_CLEARED_KEY)) {
    posthog.resetGroups();
    localStorage.setItem(LEGACY_GROUPS_CLEARED_KEY, "1");
  }
  registerAppContext();
  // We install via the npm package, not the <script> snippet, so posthog-js
  // doesn't auto-assign `window.posthog` the way the snippet does. Expose it
  // for console debugging (e.g. `posthog.config.autocapture`), matching the
  // snippet install's default behavior.
  (window as typeof window & { posthog: typeof posthog }).posthog = posthog;
}

export const identifyUser = (
  distinctId: string | undefined | null,
  properties?: Record<string, unknown>
): void => {
  if (!isPostHogEnabled || !distinctId) return;
  posthog.identify(distinctId, properties);
};

// Role can't ride along in registerAppContext — it isn't known until Keycloak
// resolves the session, so this is called from the sign-in path instead. A
// super property rather than a person property on purpose: person-property
// breakdowns in PostHog resolve to the *current* value, so a promotion would
// retroactively rewrite the role on every past event, whereas register()
// stamps each event at capture time. posthog.reset() on sign-out clears it
// (persistence.clear()), and the next sign-in re-registers it, so a role
// never leaks across users sharing a tab.
export const registerUserRole = (userRole: UserRole): void => {
  if (!isPostHogEnabled) return;
  posthog.register({ user_role: userRole });
};

// Turns session recording on/off for the current route — call start on mount
// and stop on unmount from a route whose replay masking has been reviewed
// (currently only Active Leads — see the disable_session_recording comment
// in initializePostHog above). Do not call start() from a new page without
// first reviewing what it would expose in an unmasked replay.
export const startSessionRecording = (): void => {
  if (!isPostHogEnabled) return;
  posthog.startSessionRecording();
};

export const stopSessionRecording = (): void => {
  if (!isPostHogEnabled) return;
  posthog.stopSessionRecording();
};

export const resetUser = (): void => {
  if (!isPostHogEnabled) return;
  posthog.reset();
  registerAppContext();
};

// Every active_leads_<object>_<verb> event suffix in use — typed so a typo
// at a call site is a compile error instead of a silently-missing event.
export const ACTIVE_LEADS_EVENTS = {
  Viewed: "viewed",
  PropertySelectAllClicked: "property_select_all_clicked",
  PropertySelectionApplied: "property_selection_applied",
  ApiError: "api_error",
  AttachmentDownloadClicked: "attachment_download_clicked",
  ScrollDepth: "scroll_depth",
  SearchCommitted: "search_committed",
  PageChanged: "page_changed",
  SortChanged: "sort_changed",
  FilterChanged: "filter_changed",
  KpiCardClicked: "kpi_card_clicked",
  RefreshClicked: "refresh_clicked",
  ExportClicked: "export_clicked",
  LeadDrawerOpened: "lead_drawer_opened",
  LeadDrawerClosed: "lead_drawer_closed",
} as const;

type ActiveLeadsEventSuffix =
  (typeof ACTIVE_LEADS_EVENTS)[keyof typeof ACTIVE_LEADS_EVENTS];

// Enforces the active_leads_<object>_<verb> naming convention from the plan doc (§5.4).
export const trackActiveLeadsEvent = (
  eventSuffix: ActiveLeadsEventSuffix,
  properties?: Record<string, unknown>
): void => {
  if (!isPostHogEnabled) return;
  posthog.capture(`active_leads_${eventSuffix}`, properties);
};

// Intent-level events for Performance Analysis — the page shell plus both built
// tabs, Monthly Analysis and Grids. `table_downloaded` is shared by the two and
// told apart by its `table_name` property, not by a separate event.
// Suffixes are typed for the same reason as the Active Leads ones above: the
// event names are a contract with the PostHog insights built on them, so a
// typo has to be a compile error rather than a silently-missing event.
export const REVENUE_ANALYSIS_EVENTS = {
  Opened: "opened",
  PropertySwitched: "property_switched",
  CompareToChanged: "compare_to_changed",
  TableDownloaded: "table_downloaded",
  GridsOpened: "grids_opened",
  GridValueModeChanged: "grid_value_mode_changed",
} as const;

type RevenueAnalysisEventSuffix =
  (typeof REVENUE_ANALYSIS_EVENTS)[keyof typeof REVENUE_ANALYSIS_EVENTS];

export const trackRevenueAnalysisEvent = (
  eventSuffix: RevenueAnalysisEventSuffix,
  properties?: Record<string, unknown>
): void => {
  if (!isPostHogEnabled) return;
  posthog.capture(`revenue_analysis_${eventSuffix}`, properties);
};

export { posthog };
